import { Icon, ospThemes, OspToast } from "@ospweb/sdk-ui";
import { useMemoizedFn } from "ahooks";
import { cn } from "utils";
import { EdgenModalCreateAble } from "../../EdgenModal";
import { MenuSearchModalCreateAble } from "../../Menu/MenuSearchModal";
import { AddAssetsModalCreateAbleProps } from "./types";
import { AssetsList } from "./AssetsList";
import { useRecommendAssets } from "./useRecommendAssets";
import { useAddAssetsWithBatchAdd } from "./useAddAssetsWithBatchAdd";
import Translations from "@translate/components/CoPilot/AddAssets/AddAssetsModal";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

interface AddAssetsModalProps {
  onClose: () => void;
  isMobile?: boolean;
  onCompleted?: () => void;
}

const AddAssetsModal: React.FC<AddAssetsModalProps> = ({
  onClose,
  isMobile = false,
  onCompleted,
}) => {
  const { stocks: recommendedStocks, crypto: recommendedCrypto } =
    useRecommendAssets();

  const {
    selectedByCategory,
    selectedCount,
    handleSelectionChange,
    isAdding,
    handleAddClick,
  } = useAddAssetsWithBatchAdd({
    recommendedStocks,
    recommendedCrypto,
    onSuccess: () => {
      onCompleted?.();
      onClose?.();
    },
    onError: () => {
      OspToast.error({ content: I18n.t("add assets failed") });
    },
  });

  const handleAdd = async () => {
    await handleAddClick();
  };

  const handleAddOthers = useMemoizedFn(async () => {
    await MenuSearchModalCreateAble({
      type: "portfolio",
      onCompleted: onCompleted,
    });
  });

  return (
    <div
      className={cn(
        "flex flex-col w-full h-full px-6 pt-8",
        isMobile ? "" : "pb-6",
      )}
    >
      {!isMobile && (
        <Icon
          data-openreplay-label="close"
          name="close"
          size={24}
          color={ospThemes.colors.content_primary}
          className="absolute top-[14px] right-[14px] cursor-pointer"
          onClick={onClose}
        />
      )}
      <div className={isMobile ? "flex flex-row items-center h-14" : ""}>
        {isMobile && (
          <Icon
            data-openreplay-label="left-arrow-alt"
            name="left-arrow-alt"
            size={24}
            color={ospThemes.colors.content_primary}
            className="mr-2"
            onClick={onClose}
          />
        )}
        <span className="headline_regular_s text-content_primary">
          {I18n.t("Add More Asset")}
        </span>
      </div>
      <span className="body_l text-content_secondary">
        {I18n.t(
          "Go on, add more. I'll make sure it all fits perfectly into your strategy, and I'll understand your investment style even better.",
        )}
      </span>
      {/* 资产列表区域 */}
      <div
        className={cn(
          "flex md:flex-row flex-col gap-4 overflow-y-auto no-scrollbar",
          isMobile ? "mt-4 h-full" : "mt-6",
        )}
      >
        {/* Stocks 分类 */}
        <AssetsList
          title={I18n.t("Stocks")}
          category="stocks"
          assets={recommendedStocks}
          selectedIds={selectedByCategory.stocks}
          onSelectionChange={handleSelectionChange}
          type="modal"
        />

        {/* Crypto 分类 */}
        <AssetsList
          title={I18n.t("Crypto")}
          category="crypto"
          assets={recommendedCrypto}
          selectedIds={selectedByCategory.crypto}
          onSelectionChange={handleSelectionChange}
          type="modal"
        />
      </div>
      {/* 底部按钮区域 */}
      <div
        className={cn(
          "flex justify-end gap-4",
          isMobile ? "mt-0 mb-6 mt-4" : "mt-8",
        )}
      >
        <button
          aria-label="Add Assets Modal"
          data-openreplay-label="Add Assets Modal Button"
          id="btn-add-assets-modal"
          onClick={handleAddOthers}
          disabled={selectedCount === 0 || isAdding}
          className="button secondary_button lg_button button_l h-12 text-content_primary font-bold border-[1.25px] border-border_white_transparent20 max-w-[120px] w-[120px]"
        >
          <span>{I18n.t("Add Others")}</span>
        </button>
        <button
          aria-label="Add Assets Modal"
          data-openreplay-label="Add Assets Modal Button"
          id="btn-add-assets-modal-2"
          type="button"
          onClick={handleAdd}
          disabled={selectedCount === 0 || isAdding}
          className="button primary_button lg_button button_l h-12 text-content_inverse_primary font-bold max-w-[120px] w-[120px]"
        >
          <span>
            {I18n.t("Add")}
            {`(${selectedCount})`}
          </span>
        </button>
      </div>
    </div>
  );
};

export const AddAssetsModalCreateAble = async (
  props?: AddAssetsModalCreateAbleProps,
): Promise<void> => {
  const { isMobile, onCompleted } = props ?? {
    isMobile: false,
    onCompleted: () => {},
  };

  const onClose = () => {
    EdgenModalCreateAble.end(false);
  };

  return new Promise(() => {
    EdgenModalCreateAble.call({
      children: (
        <AddAssetsModal
          onClose={onClose}
          isMobile={isMobile}
          onCompleted={onCompleted}
        />
      ),
      containerClassName: cn(
        "bg-background_solid_secondary",
        isMobile ? "w-full h-full" : "w-full w-[560px] h-[566px]",
      ),
      contentStyle: {
        borderRadius: 16,
        width: "100%",
        height: "100%",
        padding: 0,
        overflow: "hidden",
      },
      containerStyle: {
        maxHeight: "100%",
      },
      destroyOnClose: true,
      onCancel: onClose,
      showClose: false,
    });
  });
};
